/*
 * RebateAgreementDetailPage - Main page for rebate agreement details
 *
 * UI/UX DESCRIPTION:
 * - Dashboard layout with header and collapsible sections
 * - Status & Forecast section with charts and cards
 * - Rebate Rules section with expandable rule cards
 * - Product Groups section with searchable table
 * - Payments section with payment records table
 * - Invoices section with invoice history
 * - Modal dialogs for creating/editing entities
 *
 * FUNCTIONALITY:
 * - View and manage rebate agreement details
 * - Create, edit, and delete rebate rules
 * - Manage product groups for the supplier
 * - Record and view supplier payments
 * - View rebate invoices and documents
 * - Collapsible sections for better organization
 * - Real-time data loading and error handling
 */

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import { scroller, Element } from 'react-scroll';
import {
  CaretDownIcon,
  FolderIcon,
  FileTextIcon,
  CreditCardIcon,
  ReceiptIcon,
  TrashIcon,
  PlusIcon,
  FileIcon,
  CircleNotchIcon,
  WarningCircleIcon,
  LightningIcon,
} from '@phosphor-icons/react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { Button } from 'src/components/ui/button';
import { Card, CardContent, CardHeader } from 'src/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { Badge } from 'src/components/ui/badge';
import PageNavigator, {
  NavigationSection,
} from 'src/components/common/PageNavigator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';

// API Hooks
import useRebateAgreement from 'src/api/rebates/useRebateAgreement';
import { useProductGroupsBySupplierWithTags } from 'src/api/rebates/useProductGroups';
import useRebateRulesByAgreement from 'src/api/rebates/useRebateRules';
import { useSupplierPaymentsByAgreement } from 'src/api/rebates/useSupplierPayments';
import { useRebateInvoicesByAgreement } from 'src/api/rebates/useRebateInvoices';
import useDivisions from 'src/api/organization/useDivisions';
import { useGetDocument } from 'src/api/core/documents/useGetDocument';
import {
  useListVersions,
  useFinalizeVersion,
  useDeleteVersion,
} from 'src/api/rebates/useRebateVersions';
import useReprocessAgreementWithSupplier from 'src/api/rebates/useReprocessAgreementWithSupplier';
import { useSupplier } from 'src/api/rpc/hooks/accounts-payable/useSupplier';

// Components
import AgreementHeader from 'src/components/rebates/agreement-detail/AgreementHeader';
import StatusTab from 'src/components/rebates/agreement-detail/StatusTab';
import RebateRulesContainer from 'src/components/rebates/agreement-detail/RebateRulesContainer';
import { ProductGroupsSection } from 'src/components/rebates/agreement-detail/ProductGroupsSection';
import { VendorReportedAmountsSection } from 'src/components/rebates/agreement-detail/VendorReportedAmountsSection';
import GentlyTable from 'src/components/common/GentlyTable';
import ProductGroupModal from 'src/components/rebates/ProductGroupModal';
import SupplierPaymentModal from 'src/components/rebates/SupplierPaymentModal';
import { PaymentAllocationModal } from 'src/components/rebates/PaymentAllocationModal';
import { RebateReconciliationSection } from 'src/components/rebates/RebateReconciliationSection';
import { DeleteAgreementConfirmDialog } from 'src/components/rebates/DeleteAgreementConfirmDialog';
import RawFilePreviewPopout from 'src/components/common/RawFilePreviewPopout';
import type { SupplierPayment, RebateRuleWithId } from 'src/api/rebates/types';

// Custom Hook
import { useAgreementDetailState } from 'src/hooks/useAgreementDetailState';
import config from 'src/config';
import { formatPaymentDate } from 'src/utils/dateUtils';
import { useAgreementForecastAutoGenerate } from '@/api/rebates/useRebateForecast';
import SupplierPill from '@/components/company/SupplierPill';
import { useInvoiceDeleteModal } from '@/hooks/useInvoiceDeleteModal';
import { DeleteRebateInvoiceModal } from '@/components/rebates/DeleteRebateInvoiceModal';
import { VersionSelector } from 'src/components/rebates/VersionSelector';
import { VersionBanner } from 'src/components/rebates/VersionBanner';
import { CSVMigrationWizard } from 'src/components/rebates/CSVMigrationWizard';
import { formatCurrency } from '@erp/shared-utils';
import { useCompanyBySupplier } from '@/api/core/companies/useCompanyBySupplier';
import type { CalculatedBreakdown } from '@/api/rebates/types';
import type { UnifiedForecastResult } from '@erp/shared-types';
import { REBATE_SUPPLIER_PAYMENT_TYPE_LABELS } from '@erp/shared-types';
import type { RebateSupplierPaymentType } from '@erp/shared-types';
import { SupplierChangeConfirmationModal } from '@/components/rebates/SupplierChangeConfirmationModal';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useBulkEnumOptionAssociations } from '@/api/core/enumOptionAssociations/useEnumOptionAssociations';
import useCoreEnums from '@/api/core/core-enums/useCoreEnums';
import { CloseAgreementModal } from '@/components/rebates/CloseAgreementModal';
import useCloseRebateAgreement from '@/api/rebates/useCloseRebateAgreement';
import { useRebateLineItemsByAgreement } from '@/api/rebates/useRebateLineItems';
import { calculateRuleVolumes } from '@/utils/rebateLineItemUtils';
import { ForecastControlsFAB } from '@/components/rebates/ForecastControlsFAB';
import { useRebateFilters } from '@/components/rebates/hooks/useRebateFilters';
import { LineType } from '@/types/rebateAnalytics';
import {
  PREDICTION_ALGORITHMS,
  PredictionAlgorithm,
  ALL_PREDICTION_ALGORITHMS,
} from '@/components/rebates/ForecastCard';
import {
  ForegroundAgentChat,
  FilterUpdate,
} from '@/components/common/ForegroundAgentChat';

const RebateAgreementDetailPage: React.FC = () => {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();

  // Get version from URL search params
  const searchParams = useSearch({ strict: false }) as {
    version?: string;
  };
  const versionParam = searchParams.version;
  const requestedVersionNumber = versionParam
    ? parseInt(versionParam, 10)
    : null;

  // Version state
  const [isMigrationWizardOpen, setIsMigrationWizardOpen] = useState(false);

  // Multi-year agreement: year selection state
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Cumulative vs month-by-month toggle (shared across forecast and volume tables)
  const [isCumulative, setIsCumulative] = useState(true);

  // Line visibility and primary selection state (for FAB)
  // Include all prediction algorithms by default instead of 'predictions'
  const [visibleLines, setVisibleLines] = useState<Set<string>>(
    new Set([
      'estimates',
      ...ALL_PREDICTION_ALGORITHMS,
      'reported',
      'previousYear',
    ]),
  );
  const [primaryLine, setPrimaryLine] = useState<LineType>('sarima');

  // Note: Prediction algorithm visibility is now controlled via visibleLines
  // Each algorithm (linear-regression, ai-forecasting, etc.) is directly in visibleLines

  // Data fetching - get agreement first to get original_agreement_id
  const {
    data: agreement,
    isLoading,
    error,
    refetch: refetchAgreement,
  } = useRebateAgreement(id || '', !!id);

  // Fetch rebate rules - needed for displayRules
  const {
    data: rebateRules,
    isLoading: rebateRulesLoading,
    refetch: refetchRebateRules,
  } = useRebateRulesByAgreement(id || '', !!id);

  // Prepare entities for bulk tag fetching
  const ruleTagEntities = useMemo(() => {
    if (!rebateRules || rebateRules.length === 0) return undefined;
    return rebateRules.map((rule) => ({
      entityType: 'rebate_rule',
      entityId: rule.id,
    }));
  }, [rebateRules]);

  // Fetch tags for all rebate rules
  const { data: ruleTagAssociations } = useBulkEnumOptionAssociations(
    ruleTagEntities,
    !!ruleTagEntities && ruleTagEntities.length > 0,
  );

  // Fetch enum definition for tag display names
  const { data: rebateRuleTagsEnum } = useCoreEnums({
    name: 'Rebate Rule Tags',
    includeOptions: true,
  });

  // Create map of ruleId -> tag names (array of tag names)
  const ruleTagsMap = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!rebateRules || !ruleTagAssociations) return map;

    // Create a map of tag ID -> tag name from enum
    const tagIdToName = new Map<string, string>();
    rebateRuleTagsEnum?.edges?.[0]?.options?.forEach((option) => {
      if (option.name) {
        tagIdToName.set(option.id, option.name);
      }
    });

    // Build ruleId -> tag names map
    rebateRules.forEach((rule) => {
      const ruleTags = ruleTagAssociations.filter(
        (assoc) =>
          assoc.entityType === 'rebate_rule' && assoc.entityId === rule.id,
      );
      const tagNames = ruleTags
        .map((assoc) => tagIdToName.get(assoc.coreEnumOptionId))
        .filter((name): name is string => !!name);
      if (tagNames.length > 0) {
        map.set(rule.id, tagNames);
      }
    });

    return map;
  }, [rebateRules, ruleTagAssociations, rebateRuleTagsEnum]);

  // Create map of tag name -> tag ID (for reverse lookup)
  const tagNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    rebateRuleTagsEnum?.edges?.[0]?.options?.forEach((option) => {
      if (option.name) {
        map.set(option.name, option.id);
      }
    });
    return map;
  }, [rebateRuleTagsEnum]);

  // Multi-year agreement support: calculate available years
  const availableYears = useMemo(() => {
    if (!agreement) return [];
    const start = new Date(agreement.start_date);
    const end = new Date(agreement.end_date);
    const years: number[] = [];
    for (
      let year = start.getUTCFullYear();
      year <= end.getUTCFullYear();
      year++
    ) {
      years.push(year);
    }
    return years;
  }, [agreement]);

  const isMultiYear = availableYears.length > 1;

  // Auto-default to current year when agreement loads (for multi-year agreements)
  // Using "adjust state during render" pattern instead of useEffect
  const [prevAvailableYears, setPrevAvailableYears] = useState<number[]>([]);
  if (
    isMultiYear &&
    selectedYear === null &&
    availableYears.length > 0 &&
    availableYears !== prevAvailableYears
  ) {
    setPrevAvailableYears(availableYears);
    const currentYear = new Date().getFullYear();
    const defaultYear = availableYears.includes(currentYear)
      ? currentYear
      : availableYears[availableYears.length - 1];
    setSelectedYear(defaultYear);
  }

  // Calculate effective date range based on selected year
  // For multi-year: intersect selected calendar year with agreement dates
  // For single-year: use full agreement dates
  const effectiveDateRange = useMemo(() => {
    if (!agreement) return null;

    // Single-year agreement: use full agreement dates
    if (!isMultiYear) {
      return {
        startDate: agreement.start_date,
        endDate: agreement.end_date,
      };
    }

    // Multi-year with no selection yet: wait for auto-default
    if (selectedYear === null) {
      return null;
    }

    // Multi-year: intersect selected calendar year with agreement dates
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${selectedYear}-12-31`;

    return {
      startDate:
        agreement.start_date > yearStart ? agreement.start_date : yearStart,
      endDate: agreement.end_date < yearEnd ? agreement.end_date : yearEnd,
    };
  }, [agreement, isMultiYear, selectedYear]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  // Fetch versions list using original_agreement_id from the agreement
  const {
    data: versions,
    isLoading: versionsLoading,
    refetch: refetchVersions,
  } = useListVersions(
    agreement?.original_agreement_id || '',
    !!agreement?.original_agreement_id,
  );

  // Determine which version to display
  const latestVersion = versions?.reduce(
    (latest, current) =>
      current.versionNumber > latest.versionNumber ? current : latest,
    versions[0],
  );

  // If no version param, default to latest version
  const targetVersionNumber =
    requestedVersionNumber || latestVersion?.versionNumber || 1;

  // Use the current agreement's version number as the "current" version
  const currentVersionNumber = agreement?.version_number || 1;

  // Function to change version via URL
  const handleVersionChange = (versionNumber: number) => {
    if (!versions || !latestVersion) return;

    const targetVersion = versions.find(
      (v) => v.versionNumber === versionNumber,
    );
    if (!targetVersion) return;

    // Navigate to the target version
    // If it's the latest version, don't include version param
    navigate({
      to: '/rebates/agreements/$id',
      params: { id: targetVersion.id },
      search: {
        version:
          versionNumber !== latestVersion.versionNumber
            ? String(versionNumber)
            : undefined,
      },
    });
  };

  // Function to switch to latest version
  const handleSwitchToLatest = () => {
    if (!latestVersion) return;

    navigate({
      to: '/rebates/agreements/$id',
      params: { id: latestVersion.id },
      search: {}, // No version param for latest
    });
  };

  // State for document preview
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(0);

  // Refs for navigation (must be at top level)
  const headerRef = useRef<HTMLDivElement>(null!);
  const statusRef = useRef<HTMLDivElement>(null!);
  const reconciliationRef = useRef<HTMLDivElement>(null!);
  const rulesRef = useRef<HTMLDivElement>(null!);
  const productGroupsRef = useRef<HTMLDivElement>(null!);
  const paymentsRef = useRef<HTMLDivElement>(null!);
  const invoicesRef = useRef<HTMLDivElement>(null!);
  const documentRef = useRef<HTMLDivElement>(null!);

  // Data fetching (agreement already fetched above for version info)
  const {
    data: allProductGroups,
    isLoading: productGroupsLoading,
    refetch: refetchProductGroups,
  } = useProductGroupsBySupplierWithTags(
    agreement?.supplier.id || '',
    !!agreement?.supplier.id,
  );

  const {
    data: supplierPayments,
    isLoading: paymentsLoading,
    refetch: refetchPayments,
  } = useSupplierPaymentsByAgreement(id || '', !!id);

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    refetch: refetchInvoices,
  } = useRebateInvoicesByAgreement(id || '', undefined, !!id);

  // Fetch line items for volume calculation (uses effective date range for multi-year)

  const { data: lineItemsData, isLoading: lineItemsLoading } =
    useRebateLineItemsByAgreement(
      id || '',
      effectiveDateRange
        ? {
            startDate: effectiveDateRange.startDate,
            endDate: effectiveDateRange.endDate,
          }
        : undefined,
      !!id && !!effectiveDateRange,
    );

  const { data: divisions } = useDivisions(true);
  // Fetch company for supplier to get defaultTaxRate
  const { data: companyData } = useCompanyBySupplier(
    agreement?.supplier?.id,
    !!agreement?.supplier?.id,
  );

  // Document fetching - on-demand POST request
  const { mutate: fetchDocument } = useGetDocument();

  // Invoice delete modal
  const invoiceDeleteModal = useInvoiceDeleteModal({
    agreementId: id || '',
    onSuccess: () => {
      // Refresh invoice list after deletion
      refetchInvoices();
      // Regenerate forecast data
      refetchForecast();
    },
  });

  // Version management mutations
  const finalizeMutation = useFinalizeVersion(
    agreement?.original_agreement_id || '',
    currentVersionNumber,
  );
  const deleteMutation = useDeleteVersion(
    agreement?.original_agreement_id || '',
    currentVersionNumber,
  );
  // Validation: Check if agreement can be finalized
  const validationErrors = useMemo(() => {
    if (!rebateRules || rebateRules.length === 0) {
      return ['At least one rebate rule is required'];
    }

    const errors: string[] = [];

    for (const rule of rebateRules) {
      // Skip manual rules - they have different validation requirements
      if (rule.isManual) continue;

      const ruleErrors: string[] = [];

      // Check 1: Growth rules must have growthBaseline
      if (
        rule.thresholdType === 'growth' &&
        (!rule.growthBaseline || rule.growthBaseline === '0.00')
      ) {
        ruleErrors.push('missing growth baseline');
      }

      // Check 2: Growth/volume rules must have at least one tier
      if (
        (rule.thresholdType === 'growth' || rule.thresholdType === 'volume') &&
        (!rule.tiers || rule.tiers.length === 0)
      ) {
        ruleErrors.push('has no tiers defined');
      }

      // Check 3: Growth/volume rules must have at least one division
      if (
        (rule.thresholdType === 'growth' || rule.thresholdType === 'volume') &&
        (!rule.divisions || rule.divisions.length === 0)
      ) {
        ruleErrors.push('has no applicable divisions');
      }

      if (ruleErrors.length > 0) {
        errors.push(`"${rule.name}": ${ruleErrors.join(', ')}`);
      }
    }

    return errors;
  }, [rebateRules]);

  // State management hook
  const {
    expandedSections,
    expandedRules,
    productGroupModal,
    rebateRuleModal,
    paymentModal,
    agreementDeleteModal,
    handleAccordionChange,
    toggleRuleExpansion,
    productGroupHandlers,
    rebateRuleHandlers,
    paymentHandlers,
    agreementHandlers,
    handleBack,
  } = useAgreementDetailState({
    agreementId: id || '',
    refetchAgreement,
    refetchProductGroups,
    refetchRebateRules,
    refetchPayments,
  });

  // Callback to collapse status section when all estimates are 0
  const handleForecastDataReceived = useCallback(
    (data: UnifiedForecastResult) => {
      if (!data) return;

      // Check if all estimates are 0
      const allEstimatesZero =
        data.estimates.length === 0 ||
        data.estimates.every(
          (point) => Math.abs(point.totalRebateAmount) < 0.01,
        );

      // If all estimates are 0 and section is expanded, collapse it
      if (allEstimatesZero && expandedSections.has('status')) {
        const mockEvent = {} as React.SyntheticEvent;
        handleAccordionChange('status')(mockEvent, false);
      }
    },
    [expandedSections, handleAccordionChange],
  );

  const {
    data: forecastData,
    isLoading: forecastLoading,
    refetch: refetchForecast,
  } = useAgreementForecastAutoGenerate(
    id || '',
    effectiveDateRange
      ? {
          startDate: effectiveDateRange.startDate,
          endDate: effectiveDateRange.endDate,
        }
      : undefined,
    handleForecastDataReceived,
  );

  // Get available prediction algorithms from forecast data
  const availablePredictionAlgorithms = useMemo((): PredictionAlgorithm[] => {
    const predictions = forecastData?.predictions;
    if (!predictions) {
      return ['linear-regression'] as PredictionAlgorithm[];
    }
    const keys = Object.keys(predictions);
    const algorithms = keys.filter(
      (key): key is PredictionAlgorithm => key in PREDICTION_ALGORITHMS,
    );
    return algorithms.length > 0
      ? algorithms
      : (['linear-regression'] as PredictionAlgorithm[]);
  }, [forecastData?.predictions]);

  // Get available tag names for the filter UI (reuse rebateRuleTagsEnum from above)
  const availableTags = useMemo(() => {
    if (!rebateRuleTagsEnum?.edges[0]?.options) return [];
    return rebateRuleTagsEnum.edges[0].options;
  }, [rebateRuleTagsEnum?.edges]);

  // Use rebate filters hook to manage all filter state
  const {
    filterFns,
    filterGroups,
    activeFilterCount,
    activeFilterNames,
    clearAllFilters,
    selectedTagIds,
    selectedRuleIds,
    selectedDivisionIds,
  } = useRebateFilters({
    rules: forecastData?.rules,
    rebateRules,
    divisions: divisions?.divisions,
    availableTags,
  });

  // Check if FAB controls are at default values
  const defaultVisibleLines = useMemo(() => {
    const defaultSet = new Set(['estimates', 'predictions', 'reported']);
    return defaultSet;
  }, []);

  const isResetAvailable = useMemo(() => {
    // Check if cumulative is non-default (default is true)
    if (!isCumulative) return true;

    // Check if any filters are active
    if (activeFilterCount > 0) return true;

    // Check if visible lines differ from default
    if (visibleLines.size !== defaultVisibleLines.size) return true;
    for (const line of defaultVisibleLines) {
      if (!visibleLines.has(line)) return true;
    }

    // Check if primary line is non-default (default is 'sarima')
    if (primaryLine !== 'sarima') return true;

    return false;
  }, [
    isCumulative,
    activeFilterCount,
    visibleLines,
    defaultVisibleLines,
    primaryLine,
  ]);

  const handleResetFAB = useCallback(() => {
    setIsCumulative(true);
    clearAllFilters();
    setVisibleLines(
      new Set(['estimates', ...ALL_PREDICTION_ALGORITHMS, 'reported']),
    );
    setPrimaryLine('sarima');
  }, [clearAllFilters]);

  // Handler for AI-driven filter updates
  const handleAIFilterUpdate = useCallback(
    (filters: FilterUpdate) => {
      if (filters.clearAllFilters) {
        handleResetFAB();
        return;
      }

      if (filters.cumulative !== undefined) {
        setIsCumulative(filters.cumulative);
      }

      if (filters.visibleLines !== undefined) {
        // Expand 'predictions' shorthand to all prediction algorithms
        const expandedLines = filters.visibleLines.flatMap((line) =>
          line === 'predictions' ? ALL_PREDICTION_ALGORITHMS : [line],
        );

        // Enforce gating: if any prediction algorithm is in the list, ensure estimates is also included
        const hasPredictionAlgorithm = expandedLines.some((line) =>
          ALL_PREDICTION_ALGORITHMS.includes(line as PredictionAlgorithm),
        );
        const hasEstimates = expandedLines.includes('estimates');

        if (hasPredictionAlgorithm && !hasEstimates) {
          expandedLines.unshift('estimates');
        }

        setVisibleLines(new Set(expandedLines));
      }

      if (filters.primaryLine !== undefined) {
        setPrimaryLine(filters.primaryLine);
      }

      // Handle division filtering by name
      if (filters.divisionNames !== undefined) {
        if (filters.divisionNames.length === 0) {
          // Clear division filters - update filterGroups via the onChange handler
          const divisionGroup = filterGroups.find(
            (g) => g.label === 'Divisions',
          );
          if (divisionGroup) {
            divisionGroup.onChange([]);
          }
        } else {
          // Find divisions by name and select them
          const divisionGroup = filterGroups.find(
            (g) => g.label === 'Divisions',
          );
          if (divisionGroup) {
            const matchingDivisions = divisionGroup.options.filter((opt) =>
              filters.divisionNames!.some(
                (name) =>
                  opt.label.toLowerCase().includes(name.toLowerCase()) ||
                  name.toLowerCase().includes(opt.label.toLowerCase()),
              ),
            );
            divisionGroup.onChange(matchingDivisions);
          }
        }
      }
    },
    [handleResetFAB, filterGroups],
  );

  // Allocation modal state
  const [allocationModalOpen, setAllocationModalOpen] = useState(false);
  const [selectedPaymentForAllocation, setSelectedPaymentForAllocation] =
    useState<SupplierPayment | null>(null);

  function handleOpenAllocationModal(payment: SupplierPayment) {
    setSelectedPaymentForAllocation(payment);
    setAllocationModalOpen(true);
  }

  function handleCloseAllocationModal() {
    setAllocationModalOpen(false);
    setSelectedPaymentForAllocation(null);
  }

  // Supplier change modal state
  const [supplierChangeModalOpen, setSupplierChangeModalOpen] = useState(false);
  const [newSupplierId, setNewSupplierId] = useState<string | undefined>(
    undefined,
  );
  const { data: newSupplierData } = useSupplier(newSupplierId);

  // Close agreement modal state
  const [closeAgreementModalOpen, setCloseAgreementModalOpen] = useState(false);
  const closeAgreementMutation = useCloseRebateAgreement(id || '');

  const reprocessMutation = useReprocessAgreementWithSupplier(id || '');

  function handleSupplierSelect(supplierId: string | undefined) {
    if (!supplierId || !agreement || agreement.status !== 'draft') {
      return;
    }

    // If selecting the same supplier, do nothing
    if (supplierId === agreement.supplier.id) {
      return;
    }

    // Open confirmation modal
    setNewSupplierId(supplierId);
    setSupplierChangeModalOpen(true);
  }

  function handleSupplierChangeConfirm() {
    if (!newSupplierId || !agreement) {
      return;
    }

    // Call the mutation to reprocess with new supplier
    reprocessMutation.mutate(
      {
        supplierId: newSupplierId,
      },
      {
        onSuccess: (response) => {
          // Close the modal
          setSupplierChangeModalOpen(false);
          setNewSupplierId(undefined);

          // Navigate to the new agreement (same ID, but reprocessed)
          navigate({
            to: '/rebates/agreements/$id',
            params: { id: response.data.id },
          });

          // Refetch data
          refetchAgreement();
        },
        onError: () => {
          // Error is already handled by the mutation hook
          setSupplierChangeModalOpen(false);
          setNewSupplierId(undefined);
        },
      },
    );
  }

  function handleSupplierChangeCancel() {
    setSupplierChangeModalOpen(false);
    setNewSupplierId(undefined);
  }

  // Track rules with validation issues (updated by Container)
  const [rulesWithIssuesCount, setRulesWithIssuesCount] = useState(0);

  // Callback when save is complete - refetch data
  const handleSaveComplete = useCallback(() => {
    refetchRebateRules();
    refetchForecast();
  }, [refetchRebateRules, refetchForecast]);

  function handleAllocationSuccess() {
    refetchPayments();
    handleCloseAllocationModal();
  }

  // Calculate summary totals for close agreement modal
  const closeAgreementSummary = useMemo(() => {
    if (!forecastData?.estimates || !supplierPayments) {
      return { expected: 0, allocated: 0, writeOff: 0 };
    }

    // Aggregate forecast estimates
    let expected = 0;
    forecastData.estimates.forEach((estimate) => {
      estimate.breakdowns.forEach((breakdown) => {
        expected += breakdown.rebateAmount;
      });
    });

    // Aggregate payment allocations
    let allocated = 0;
    supplierPayments.forEach((payment) => {
      if (payment.allocations) {
        payment.allocations.forEach((allocation) => {
          allocated += allocation.allocatedAmount;
        });
      }
    });

    const writeOff = expected - allocated;

    return { expected, allocated, writeOff };
  }, [forecastData, supplierPayments]);

  // Handle close agreement
  function handleOpenCloseAgreementModal() {
    setCloseAgreementModalOpen(true);
  }

  function handleCloseAgreementModal() {
    setCloseAgreementModalOpen(false);
  }

  function handleConfirmCloseAgreement(reason: string, notes?: string) {
    if (!id) return;

    closeAgreementMutation.mutate(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { reason: reason as any, notes },
      {
        onSuccess: () => {
          handleCloseAgreementModal();
          refetchAgreement();
          refetchForecast();
          refetchPayments();
        },
      },
    );
  }

  function handleReopenAgreement() {
    refetchAgreement();
    refetchForecast();
    refetchPayments();
  }

  // Calculate the last data ingestion date (max date of all invoice line item ranges)
  const lastDataIngestionDate = useMemo<string | null>(() => {
    if (!invoicesData?.invoices || invoicesData.invoices.length === 0) {
      return null;
    }

    const dates = invoicesData.invoices
      .map((invoice) => invoice.lineItemDateRange?.latest)
      .filter((date): date is string => !!date);

    if (dates.length === 0) {
      return null;
    }

    // Find the maximum date
    const maxDate = dates.reduce((max, current) => {
      return current > max ? current : max;
    }, dates[0]);

    return maxDate;
  }, [invoicesData]);

  // Extract calculated breakdowns from forecast data
  // Include ALL months from forecast data (even with $0) so the date range shows all months
  const calculatedBreakdowns = useMemo<CalculatedBreakdown[]>(() => {
    if (!forecastData?.estimates) return [];

    const breakdowns: CalculatedBreakdown[] = [];
    forecastData.estimates.forEach((point) => {
      // Format date as YYYY-MM-DD (end-of-month date)
      // Use UTC methods to avoid timezone issues
      const year = point.date.getUTCFullYear();
      const month = String(point.date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(point.date.getUTCDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      // Include all breakdowns (including zero values) so all months appear
      if (point.breakdowns && point.breakdowns.length > 0) {
        point.breakdowns.forEach((breakdown) => {
          // Filter for ESTIMATE type only (but include zero amounts)
          if (
            breakdown.calculationType === 'estimate' ||
            breakdown.calculationType === 'historical'
          ) {
            breakdowns.push({
              id: `${breakdown.ruleId}|${breakdown.divisionId}|${dateStr}`,
              ruleId: breakdown.ruleId,
              ruleName: breakdown.ruleName,
              divisionId: breakdown.divisionId,
              divisionName: breakdown.divisionName,
              date: dateStr,
              rebateAmount: breakdown.rebateAmount, // Include zero values
              calculationType: breakdown.calculationType,
            });
          }
        });
      }
      // Note: If a month has no breakdowns (empty array), we don't create entries
      // but the date will still appear in availableDates if there are breakdowns
      // from other months. Months with zero breakdowns will show empty in the table.
    });

    return breakdowns;
  }, [forecastData]);

  // Calculate total rebate per rule from calculatedBreakdowns
  const rebateTotalsByRule = useMemo(() => {
    const totals = new Map<string, number>();

    // Initialize all rules with 0
    if (rebateRules) {
      rebateRules.forEach((rule) => {
        totals.set(rule.id, 0);
      });
    }

    // Sum up breakdowns
    calculatedBreakdowns.forEach((breakdown) => {
      const currentTotal = totals.get(breakdown.ruleId) || 0;
      totals.set(breakdown.ruleId, currentTotal + breakdown.rebateAmount);
    });

    return totals;
  }, [calculatedBreakdowns, rebateRules]);

  // Calculate tier status per rule by type (estimates, predictions)
  const tierStatusByRule = useMemo(() => {
    const status = new Map<
      string,
      { estimates?: number; predictions?: number; reported?: number }
    >();

    // Get latest estimate tier per rule
    const latestEstimate = forecastData?.estimates?.slice(-1)[0];
    latestEstimate?.breakdowns.forEach((b) => {
      if (typeof b.tierApplied === 'number') {
        const existing = status.get(b.ruleId) || {};
        existing.estimates = b.tierApplied;
        status.set(b.ruleId, existing);
      }
    });

    // Get latest prediction tier per rule
    const predictions = forecastData?.predictions?.['linear-regression'];
    const latestPrediction = predictions?.slice(-1)[0];
    latestPrediction?.breakdowns.forEach((b) => {
      if (typeof b.tierApplied === 'number') {
        const existing = status.get(b.ruleId) || {};
        existing.predictions = b.tierApplied;
        status.set(b.ruleId, existing);
      }
    });

    // Get latest reported tier per rule
    const latestReported = forecastData?.reported?.slice(-1)[0];
    latestReported?.breakdowns.forEach((b) => {
      if (typeof b.tierApplied === 'number') {
        const existing = status.get(b.ruleId) || {};
        existing.reported = b.tierApplied;
        status.set(b.ruleId, existing);
      }
    });

    return status;
  }, [forecastData]);

  // Calculate volume totals per rule from line items
  const volumesByRule = useMemo(() => {
    const volumes = new Map<
      string,
      {
        qualifyingVolume: number;
        affectedVolume: number;
        discountedQualifyingVolume: number;
        discountedAffectedVolume: number;
        discountPercentage: number;
      }
    >();

    // useGetApi returns data.data, so lineItemsData is already the unwrapped data object
    if (!rebateRules || !lineItemsData?.lineItems) {
      return volumes;
    }

    const lineItems = lineItemsData.lineItems;

    rebateRules.forEach((rule) => {
      const {
        totalQualifyingVolume,
        totalAffectedVolume,
        discountedQualifyingVolume,
        discountedAffectedVolume,
        discountPercentage,
      } = calculateRuleVolumes(
        rule,
        lineItems,
        agreement?.start_date,
        agreement?.end_date,
      );
      volumes.set(rule.id, {
        qualifyingVolume: totalQualifyingVolume,
        affectedVolume: totalAffectedVolume,
        discountedQualifyingVolume,
        discountedAffectedVolume,
        discountPercentage,
      });
    });

    return volumes;
  }, [rebateRules, lineItemsData, agreement?.start_date, agreement?.end_date]);

  // Generic document viewing function
  const handleViewDocument = (documentId: string, fileName?: string) => {
    if (!documentId) {
      return;
    }
    fetchDocument(
      { documentId: documentId },
      {
        onSuccess: (response) => {
          if (response.data.signedUrl) {
            window.open(response.data.signedUrl, '_blank');
          }
        },
        onError: () => {
          // Silently handle error
        },
      },
    );
  };

  // Function to fetch document for preview
  const handleFetchDocumentForPreview = useCallback(
    async (documentIndex?: number) => {
      const documents = agreement?.documents;
      if (!documents || documents.length === 0) {
        return;
      }

      const indexToUse =
        documentIndex !== undefined ? documentIndex : selectedDocumentIndex;
      const selectedDocument = documents[indexToUse];

      if (!selectedDocument) {
        return;
      }

      setIsLoadingDocument(true);

      fetchDocument(
        { documentId: selectedDocument.id },
        {
          onSuccess: async (response) => {
            if (response.data.signedUrl) {
              try {
                // Fetch the file from the signed URL
                const fileResponse = await fetch(response.data.signedUrl);
                const blob = await fileResponse.blob();

                // Create a File object from the blob
                const file = new File(
                  [blob],
                  selectedDocument.name || 'document',
                  {
                    type: blob.type || 'application/octet-stream',
                  },
                );

                setDocumentFile(file);
              } catch (error) {
                // Silently handle error
              }
            }
            setIsLoadingDocument(false);
          },
          onError: () => {
            setIsLoadingDocument(false);
          },
        },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agreement?.documents, selectedDocumentIndex],
  );

  // Function to handle document switching
  const handleDocumentSwitch = useCallback(
    (newIndex: number) => {
      setSelectedDocumentIndex(newIndex);
      setDocumentFile(null); // Clear current document
      handleFetchDocumentForPreview(newIndex);
    },
    [handleFetchDocumentForPreview],
  );

  // Auto-load document when agreement is loaded
  useEffect(() => {
    if (agreement?.documents?.[0] && !documentFile && !isLoadingDocument) {
      handleFetchDocumentForPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement?.documents, documentFile]);

  // Reset selected document index when agreement changes
  useEffect(() => {
    setSelectedDocumentIndex(0);
    setDocumentFile(null);
  }, [agreement?.id]);

  // State for navigation sections
  const [navigationSections, setNavigationSections] = useState<
    NavigationSection[]
  >([]);

  const simpleDivisions = divisions?.divisions.map((d) => ({
    id: d.id,
    name: d.name,
    tags: d.tags?.map((t) => t.name) || [],
  }));

  // Update navigation sections when data changes
  useEffect(() => {
    // Early exit if data isn't loaded yet
    if (!agreement || isLoading) return;

    const hasForecastData =
      forecastData?.estimates && forecastData.estimates.length > 0;
    const isLoadingForecastOrPayments = forecastLoading;

    const newNavigationSections: NavigationSection[] = [
      {
        id: 'header',
        label: 'Basic Information',
        ref: headerRef,
        icon: FolderIcon,
        isExpanded: true, // Always visible
      },
      {
        id: 'document',
        label: 'Agreement Document',
        ref: documentRef,
        icon: FileTextIcon,
        hasData: !!(agreement?.documents && agreement.documents.length > 0),
        isExpanded: expandedSections.has('document'),
      },
      {
        id: 'status',
        label: 'Status & Forecast',
        ref: statusRef,
        icon: FolderIcon,
        hasData: hasForecastData && !isLoadingForecastOrPayments,
        isExpanded: expandedSections.has('status'),
      },
      {
        id: 'rebateRules',
        label: 'Rebate Rules',
        ref: rulesRef,
        icon: FileTextIcon,
        isExpanded: expandedSections.has('rebateRules'),
        subsections:
          rebateRules?.map((rule) => ({
            id: `rule-${rule.id}`,
            label: rule.name,
            ref: React.createRef<HTMLDivElement>(), // Dummy ref since we're using react-scroll Elements
          })) || [],
      },
      {
        id: 'productGroups',
        label: 'Product Groups',
        ref: productGroupsRef,
        icon: FolderIcon,
        isExpanded: expandedSections.has('productGroups'),
      },
      {
        id: 'payments',
        label: 'Payments',
        ref: paymentsRef,
        icon: CreditCardIcon,
        isExpanded: expandedSections.has('payments'),
      },
      {
        id: 'reconciliation',
        label: 'Reconciliation',
        ref: reconciliationRef,
        icon: LightningIcon,
        hasData:
          hasForecastData &&
          supplierPayments &&
          supplierPayments.length > 0 &&
          !isLoadingForecastOrPayments,
        isExpanded: expandedSections.has('reconciliation'),
      },
      {
        id: 'vendor_reported_amounts',
        label: 'Vendor Reported Amounts',
        ref: invoicesRef,
        icon: ReceiptIcon,
        isExpanded: expandedSections.has('vendor_reported_amounts'),
      },
    ];

    setNavigationSections(newNavigationSections);
  }, [
    rebateRules,
    agreement,
    expandedSections,
    forecastData,
    forecastLoading,
    paymentsLoading,
    supplierPayments,
    isLoading,
  ]);

  // Table column definitions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paymentColumns: any[] = [
    {
      id: 'date' as const,
      label: 'Payment Date',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm">{formatPaymentDate(value)}</span>
      ),
    },
    {
      id: 'amount' as const,
      label: 'Amount',
      sortable: true,
      align: 'right' as const,
      render: (value: number) => (
        <span className="text-sm font-medium">{formatCurrency(value)}</span>
      ),
    },
    {
      id: 'grossAmount' as const,
      label: 'Gross Amount',
      sortable: true,
      align: 'right' as const,
      render: (value: number, row: { grossAmount?: number | null }) => {
        if (!row.grossAmount) {
          return <span className="text-sm font-medium">-</span>;
        }
        return (
          <span className="text-sm font-medium">
            {formatCurrency(row.grossAmount || 0)}
          </span>
        );
      },
    },
    {
      id: 'Tax Withheld',
      label: 'Tax Withheld',
      sortable: true,
      align: 'right' as const,
      render: (
        value: number,
        row: {
          taxRate?: number | null;
          grossAmount?: number | null;
        },
      ) => {
        if (!row.taxRate || !row.grossAmount) {
          return <span className="text-sm font-medium">-</span>;
        }
        const taxAmount = row.grossAmount * (row.taxRate / 100);
        return (
          <span className="text-sm font-medium">
            {formatCurrency(taxAmount)} ({row.taxRate}%)
          </span>
        );
      },
    },
    {
      id: 'allocationStatus' as const,
      label: 'Allocation Status',
      sortable: true,
      // ADD THIS:
      align: 'right' as const, // ✅ Ensures consistent header + content alignment
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (value: string | undefined, row: any) => {
        const status = value || 'unallocated';
        const statusConfig = {
          unallocated: {
            label: 'Unallocated',
            color: 'bg-gray-100 text-gray-800',
          },
          partially_allocated: {
            label: 'Partial',
            color: 'bg-yellow-100 text-yellow-800',
          },
          fully_allocated: {
            label: 'Allocated',
            color: 'bg-green-100 text-green-800',
          },
          over_allocated: {
            label: 'Over-allocated',
            color: 'bg-red-100 text-red-800',
          },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <div className="flex flex-col items-end gap-1">
            {' '}
            {/* ✅ aligns with right edge */}
            <Badge className={`${config?.color} text-xs w-fit`}>
              {config?.label || status}
            </Badge>
            {row.allocatedAmount !== undefined && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {formatCurrency(row.allocatedAmount)} of{' '}
                {formatCurrency(row.amount)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'type' as const,
      label: 'Payment Type',
      sortable: true,
      align: 'right' as const,
      render: (value: string) => {
        return (
          <span className="text-sm">
            {REBATE_SUPPLIER_PAYMENT_TYPE_LABELS[
              value as RebateSupplierPaymentType
            ] || value}
          </span>
        );
      },
    },
    {
      id: 'memo' as const,
      label: 'Memo',
      sortable: true,
      align: 'right' as const,
      render: (value: string) => (
        <span
          className={`text-sm ${
            value ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          {value || '-'}
        </span>
      ),
    },
    {
      id: 'document' as const,
      label: 'Document',
      align: 'right' as const,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_: any, row: any) => (
        <div onClick={(e) => e.stopPropagation()}>
          {row.document ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={async () => {
                      try {
                        const response = await fetch(
                          `${config.apiBaseUrl}/rebates/supplier-payments/${row.id}/document`,
                          { credentials: 'include' },
                        );
                        if (response.ok) {
                          const data = await response.json();
                          window.open(data.data.signedUrl, '_blank');
                        }
                      } catch (error) {
                        // Silently handle error
                      }
                    }}
                  >
                    <FileTextIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Payment Document</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          )}
        </div>
      ),
    },
    {
      id: 'actions' as const,
      label: 'Actions',
      align: 'center' as const,
      minWidth: '120px',
      className: 'whitespace-nowrap',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_: any, row: any) => (
        <div
          className="flex gap-2 justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleOpenAllocationModal(row);
                  }}
                >
                  Allocate
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {row.allocationStatus === 'fully_allocated'
                    ? 'Payment fully allocated'
                    : 'Allocate payment to rules'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    paymentHandlers.delete(row);
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete Payment</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const invoiceColumns: any[] = [
    // Index column (sticky left)
    {
      id: 'index' as const,
      label: '#',
      sortable: false,
      minWidth: '48px',
    },
    // Vendor column with pill style
    {
      id: 'supplier' as const,
      label: 'Vendor',
      sortable: false,
      render: (
        _value: unknown,
        row: { supplier?: { id: string; name: string } },
      ) => <SupplierPill supplier={row?.supplier} />,
    },
    // Line item date range
    {
      id: 'lineItemDateRange' as const,
      label: 'Line Item Date Range',
      sortable: false,
      render: (
        _value: unknown,
        row: {
          lineItemDateRange?: { earliest: string; latest: string } | null;
        },
      ) => {
        const hasRange =
          row.lineItemDateRange?.earliest && row.lineItemDateRange?.latest;
        if (!hasRange || !row.lineItemDateRange) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        const earliestDate = formatPaymentDate(row.lineItemDateRange.earliest);
        const latestDate = formatPaymentDate(row.lineItemDateRange.latest);
        const isSameDate =
          row.lineItemDateRange.earliest === row.lineItemDateRange.latest;
        return (
          <span className="text-sm font-medium">
            {isSameDate ? earliestDate : `${earliestDate} - ${latestDate}`}
          </span>
        );
      },
    },
    // Date processed (createdAt)
    {
      id: 'createdAt' as const,
      label: 'Date Processed',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm">{formatPaymentDate(value)}</span>
      ),
    },
    // Rebateable Amount
    {
      id: 'totalRebateableAmount' as const,
      label: 'Rebateable',
      sortable: true,
      align: 'right' as const,
      render: (value: number) => (
        <span className="text-sm font-medium text-green-600">
          {formatCurrency(value)}
        </span>
      ),
    },
    // Total Amount (includes non-rebateable)
    {
      id: 'totalAmount' as const,
      label: 'Total',
      sortable: true,
      align: 'right' as const,
      render: (value: number) => (
        <span className="text-sm">{formatCurrency(value)}</span>
      ),
    },
    // Original file name(s)
    {
      id: 'files' as const,
      label: 'Original File',
      align: 'left' as const,
      render: (
        _v: unknown,
        row: {
          files?: {
            originalFiles?: Array<{ id: string; name: string }>;
            transformedFile?: { id: string; name: string };
          };
        },
      ) => {
        const files = row.files;
        const originals = files?.originalFiles || [];
        if (!originals.length) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        const first = originals[0];
        const extra = originals.length - 1;
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleViewDocument(first.id, first.name);
                    }}
                  >
                    <FileIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Original File: {first.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span
              className="text-xs max-w-60 overflow-hidden text-ellipsis whitespace-nowrap"
              title={first.name}
            >
              {first.name}
            </span>
            {extra > 0 && (
              <span className="text-xs text-muted-foreground">
                +{extra} more
              </span>
            )}
          </div>
        );
      },
    },
    // Transformed file
    {
      id: 'transformedFile' as const,
      label: 'Transformed File',
      align: 'left' as const,
      render: (
        _v: unknown,
        row: {
          files?: {
            transformedFile?: { id: string; name: string } | null;
          };
        },
      ) => {
        const transformed = row.files?.transformedFile;
        if (!transformed) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleViewDocument(transformed.id, transformed.name);
                    }}
                  >
                    <FileTextIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Transformed File: {transformed.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span
              className="text-xs max-w-60 overflow-hidden text-ellipsis whitespace-nowrap"
              title={transformed.name}
            >
              {transformed.name}
            </span>
          </div>
        );
      },
    },
    // Actions column
    {
      id: 'actions' as const,
      label: 'Actions',
      align: 'center' as const,
      minWidth: '80px',
      className: 'whitespace-nowrap',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (_: any, row: any) => (
        <div className="flex gap-2 justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    invoiceDeleteModal.initiateDelete(row.id);
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete Invoice</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  // Check if we have the minimum data needed to render the page structure
  const isInitialLoading = isLoading && !agreement;
  const hasError = error && !agreement;

  if (hasError || (!id && !isLoading)) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <AgreementHeader
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                agreement={null as any}
                isLoading={false}
                onBack={handleBack}
                rebateRulesCount={0}
                validationErrors={[]}
                lastDataIngestionDate={null}
              />
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // If we're still loading and don't have agreement yet, show loading state
  if (isLoading || !agreement) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <AgreementHeader
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                agreement={null as any}
                isLoading={true}
                onBack={handleBack}
                rebateRulesCount={0}
                validationErrors={[]}
                lastDataIngestionDate={null}
              />
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const hasForecastData =
    forecastData?.estimates && forecastData.estimates.length > 0;
  const isLoadingForecastOrPayments = forecastLoading || paymentsLoading;

  // Helper function for collapsible sections - adapts MUI accordion handler to shadcn collapsible
  const handleSectionToggle = (sectionKey: string) => (open: boolean) => {
    // Create a mock event since the original handler expects it
    const mockEvent = {} as React.SyntheticEvent;
    handleAccordionChange(sectionKey)(mockEvent, open);
  };

  // Handle navigation click
  const handleNavigateToSection = (sectionId: string) => {
    // Check if this is a subsection (rule)
    if (sectionId.startsWith('rule-')) {
      const ruleId = sectionId.replace('rule-', '');

      // Check current expansion states before modifying them
      const sectionIsExpanded = expandedSections.has('rebateRules');
      const ruleIsExpanded = expandedRules.has(ruleId);

      // Subsections cannot toggle off - always expand and navigate
      // First, make sure the rebate rules section is expanded
      if (!sectionIsExpanded) {
        const mockEvent = {} as React.SyntheticEvent;
        handleAccordionChange('rebateRules')(mockEvent, true);
      }

      // Also expand the specific rule if it's not already expanded
      if (!ruleIsExpanded) {
        toggleRuleExpansion(ruleId);
      }

      // Calculate delay based on what needed to be expanded
      const delay = !sectionIsExpanded || !ruleIsExpanded ? 600 : 100;
      setTimeout(() => {
        scroller.scrollTo(`rule-${ruleId}`, {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
          offset: -80, // Account for any fixed headers
        });
      }, delay);
      return;
    }

    // Handle regular sections
    const section = navigationSections.find((s) => s.id === sectionId);
    if (!section) {
      return;
    }

    const isExpanded = expandedSections.has(sectionId);
    const hasSubsections =
      section.subsections && section.subsections.length > 0;

    // Only sections with subsections can toggle off
    if (sectionId !== 'header' && hasSubsections) {
      if (isExpanded) {
        const mockEvent = {} as React.SyntheticEvent;
        handleAccordionChange(sectionId)(mockEvent, false);
      } else {
        const mockEvent = {} as React.SyntheticEvent;
        handleAccordionChange(sectionId)(mockEvent, true);
      }
    } else if (sectionId !== 'header' && !hasSubsections) {
      // Sections without subsections can only expand (no toggle off)
      if (!isExpanded) {
        const mockEvent = {} as React.SyntheticEvent;
        handleAccordionChange(sectionId)(mockEvent, true);
      }
      // If already expanded, just scroll (no state change)
    }

    // Always scroll to the section using react-scroll
    const delay = isExpanded ? 100 : 400; // Longer delay if expanding

    setTimeout(() => {
      scroller.scrollTo(sectionId, {
        duration: 800,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: -80, // Account for any fixed headers
      });
    }, delay);
  };

  // Handle toggle section (collapse expanded sections)
  const handleToggleSection = (sectionId: string) => {
    if (sectionId === 'header') return; // Header can't be collapsed

    const mockEvent = {} as React.SyntheticEvent;
    const isExpanded = expandedSections.has(sectionId);
    handleAccordionChange(sectionId)(mockEvent, !isExpanded);
  };

  return (
    <DashboardLayout>
      <div className="p-1">
        <div className="space-y-6 pb-24">
          {/* Version Management Section */}
          {versions && versions.length > 0 && (
            <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg">
              {/* Version Selector - Show if multiple versions */}
              {versions.length > 1 && (
                <VersionSelector
                  versions={versions}
                  currentVersionNumber={currentVersionNumber}
                  onVersionChange={handleVersionChange}
                />
              )}

              {/* Version Info - Show if single version */}
              {versions.length === 1 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    Version {versions[0]?.versionNumber ?? 1}
                  </Badge>
                  <Badge
                    variant={
                      agreement?.status === 'draft' ? 'default' : 'outline'
                    }
                  >
                    {agreement?.status}
                  </Badge>
                </div>
              )}

              {/* Version Actions */}
              {agreement && (
                <div className="flex gap-2">
                  {/* Draft Actions */}
                  {agreement.status === 'draft' && (
                    <>
                      <Button
                        onClick={async () => {
                          try {
                            await finalizeMutation.mutateAsync({});
                            await Promise.all([
                              refetchAgreement(),
                              refetchVersions(),
                            ]);
                          } catch (e) {
                            // Silently handle error
                          }
                        }}
                        disabled={finalizeMutation.isPending}
                      >
                        {finalizeMutation.isPending
                          ? 'Finalizing...'
                          : 'Finalize Version'}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={async () => {
                          try {
                            await deleteMutation.mutateAsync({});
                            navigate({
                              to: '/rebates/agreements',
                            });
                          } catch (e) {
                            // Silently handle error
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending
                          ? 'Deleting...'
                          : 'Delete Draft'}
                      </Button>
                    </>
                  )}

                  {/* Finalized Actions - Create New Version */}
                  {agreement.status === 'finalized' &&
                    latestVersion &&
                    latestVersion.status === 'finalized' && (
                      <Button
                        onClick={() => {
                          navigate({
                            to: '/rebates/agreements/$id/create-version',
                            params: { id: latestVersion.id },
                          });
                        }}
                      >
                        Create New Version
                      </Button>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Version Banner */}
          {agreement && latestVersion && (
            <VersionBanner
              currentVersionNumber={currentVersionNumber}
              latestVersionNumber={latestVersion.versionNumber}
              status={agreement.status}
              onSwitchToLatest={handleSwitchToLatest}
            />
          )}

          {/* Agreement Header Card */}
          <Element name="header">
            <div ref={headerRef}>
              <Card>
                <CardContent className="p-6">
                  <AgreementHeader
                    agreement={agreement}
                    isLoading={false}
                    onBack={handleBack}
                    onViewDocument={handleViewDocument}
                    onUpdate={refetchAgreement}
                    onDelete={agreementHandlers.delete}
                    onSupplierChange={handleSupplierSelect}
                    onForecastReset={refetchForecast}
                    rebateRulesCount={rebateRules?.length || 0}
                    validationErrors={validationErrors}
                    lastDataIngestionDate={lastDataIngestionDate}
                    selectedYear={selectedYear}
                    onYearChange={handleYearChange}
                  />
                </CardContent>
              </Card>
            </div>
          </Element>
          {/* Document Section */}
          {agreement?.documents && agreement.documents.length > 0 && (
            <Element name="document">
              <div ref={documentRef}>
                <Card>
                  <Collapsible
                    open={expandedSections.has('document')}
                    onOpenChange={handleSectionToggle('document')}
                  >
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center justify-between w-full mr-4">
                            <div className="flex items-center gap-3">
                              <FileTextIcon className="h-5 w-5 text-primary" />
                              <h2 className="text-lg font-semibold">
                                Agreement Documents (
                                {agreement.documents?.length || 0})
                              </h2>
                            </div>
                            {agreement.documents &&
                              agreement.documents.length > 0 && (
                                <span className="text-sm text-muted-foreground">
                                  {agreement.documents.length} file
                                  {agreement.documents.length !== 1 ? 's' : ''}
                                </span>
                              )}
                          </div>
                          <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180 flex-shrink-0" />
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        {/* Document Switcher - Only show if multiple documents */}
                        {agreement.documents &&
                          agreement.documents.length > 1 && (
                            <div className="mb-4">
                              <Tabs
                                value={selectedDocumentIndex.toString()}
                                onValueChange={(value) =>
                                  handleDocumentSwitch(parseInt(value))
                                }
                              >
                                <TabsList className="inline-flex h-9 items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground">
                                  {agreement.documents.map((doc, index) => (
                                    <TabsTrigger
                                      key={doc.id}
                                      value={index.toString()}
                                      className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
                                    >
                                      <span className="truncate max-w-[120px]">
                                        {doc.name}
                                      </span>
                                    </TabsTrigger>
                                  ))}
                                </TabsList>
                              </Tabs>
                            </div>
                          )}

                        {/* Document Preview */}
                        {isLoadingDocument ? (
                          <div className="flex justify-center p-6">
                            <CircleNotchIcon className="h-6 w-6 animate-spin" />
                            <span className="ml-2 text-sm text-muted-foreground">
                              Loading document...
                            </span>
                          </div>
                        ) : documentFile ? (
                          <RawFilePreviewPopout
                            selectedFiles={[documentFile]}
                            className="mt-0"
                            showCard={false}
                          />
                        ) : (
                          <div className="text-center p-6">
                            <p className="text-sm text-muted-foreground">
                              No document available for preview
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              </div>
            </Element>
          )}
          {/* Status & Forecast Section - Show if there's data (even if all zeros) or loading */}
          <Element name="status">
            <div ref={statusRef}>
              {(hasForecastData || forecastData) &&
              !isLoadingForecastOrPayments ? (
                <Card>
                  <Collapsible
                    open={expandedSections.has('status')}
                    onOpenChange={handleSectionToggle('status')}
                  >
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FolderIcon className="h-5 w-5 text-primary" />
                            <h2 className="text-lg font-semibold">
                              Status & Forecast
                            </h2>
                          </div>
                          <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        <StatusTab
                          agreementId={id || ''}
                          forecastData={forecastData}
                          forecastLoading={forecastLoading}
                          divisions={divisions?.divisions}
                          rebateRules={rebateRules}
                          ruleTagsMap={ruleTagsMap}
                          tagNameToIdMap={tagNameToIdMap}
                          isCumulative={isCumulative}
                          onCumulativeChange={setIsCumulative}
                          visibleLines={visibleLines}
                          onVisibleLinesChange={setVisibleLines}
                          primaryLine={primaryLine}
                          onPrimaryLineChange={(line) => {
                            console.log(line);
                            setPrimaryLine(line as LineType);
                          }}
                          filterFns={filterFns}
                          activeFilterNames={activeFilterNames}
                          currentDate={new Date('2026-04-05')}
                        />
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              ) : (
                <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <h3 className="text-lg font-medium text-muted-foreground">
                    No Status & Forecast Data
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Status and forecast information will appear here once data
                    becomes available
                  </p>
                </div>
              )}
            </div>
          </Element>

          {/* Rebate Rules Section */}
          <Element name="rebateRules">
            <div ref={rulesRef}>
              <Card>
                <Collapsible
                  open={expandedSections.has('rebateRules')}
                  onOpenChange={handleSectionToggle('rebateRules')}
                >
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center justify-between w-full mr-4">
                          <div className="flex items-center gap-3">
                            <FileTextIcon className="h-5 w-5 text-primary" />
                            <h2 className="text-lg font-semibold">
                              Rebate Rules ({rebateRules?.length || 0})
                            </h2>
                            {rulesWithIssuesCount > 0 && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="destructive"
                                      className="bg-red-100 text-red-800 hover:bg-red-200"
                                    >
                                      <WarningCircleIcon className="h-3 w-3 mr-1" />
                                      {rulesWithIssuesCount} rule
                                      {rulesWithIssuesCount > 1 ? 's' : ''} with
                                      issues
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>
                                      {rulesWithIssuesCount} rule
                                      {rulesWithIssuesCount > 1
                                        ? 's have'
                                        : ' has'}{' '}
                                      validation issues that must be fixed
                                      before finalizing
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {rebateTotalsByRule.size > 0 && (
                            <span className="text-lg font-bold text-green-600">
                              Total:{' '}
                              {formatCurrency(
                                Array.from(rebateTotalsByRule.values()).reduce(
                                  (sum, total) => sum + total,
                                  0,
                                ),
                              )}
                            </span>
                          )}
                        </div>
                        <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180 flex-shrink-0" />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent
                    forceMount
                    className="data-[state=closed]:hidden"
                  >
                    <CardContent className="pt-0">
                      <RebateRulesContainer
                        agreementId={id}
                        rebateRules={rebateRules}
                        rebateRulesLoading={rebateRulesLoading}
                        divisions={simpleDivisions || []}
                        agreementStatus={agreement.status}
                        productGroups={allProductGroups || []}
                        onSaveComplete={handleSaveComplete}
                        onRulesWithIssuesChange={setRulesWithIssuesCount}
                        invoices={invoicesData?.invoices}
                        onDownloadDocument={handleViewDocument}
                        rebateTotalsByRule={rebateTotalsByRule}
                        tierStatusByRule={tierStatusByRule}
                        volumesByRule={volumesByRule}
                        agreementStartDate={agreement.start_date}
                        agreementEndDate={agreement.end_date}
                        lineItems={lineItemsData?.lineItems}
                      />
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            </div>
          </Element>

          {/* Product Groups Section */}
          <ProductGroupsSection
            productGroupsRef={productGroupsRef}
            isExpanded={expandedSections.has('productGroups')}
            onToggle={handleSectionToggle('productGroups')}
            allProductGroups={allProductGroups}
            productGroupsLoading={productGroupsLoading}
            rebateRules={rebateRules}
            onCreateProductGroup={productGroupHandlers.create}
            onNavigateToSection={handleNavigateToSection}
            refetchProductGroups={refetchProductGroups}
          />

          {/* Payments Section */}
          <Element name="payments">
            <div ref={paymentsRef}>
              <Card>
                <Collapsible
                  open={expandedSections.has('payments')}
                  onOpenChange={handleSectionToggle('payments')}
                >
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center justify-between w-full mr-4">
                          <div className="flex items-center gap-3">
                            <CreditCardIcon className="h-5 w-5 text-primary" />
                            <h2 className="text-lg font-semibold">
                              Recorded Supplier Payments (
                              {supplierPayments?.length || 0})
                            </h2>
                          </div>
                          {supplierPayments && supplierPayments.length > 0 && (
                            <span className="text-lg font-bold text-green-600">
                              Total: $
                              {new Intl.NumberFormat('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(
                                supplierPayments.reduce(
                                  (sum, payment) => sum + payment.amount,
                                  0,
                                ),
                              )}
                            </span>
                          )}
                        </div>
                        <CaretDownIcon className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180 flex-shrink-0" />
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      {paymentsLoading ? (
                        <div className="flex justify-center p-6">
                          <CircleNotchIcon className="h-6 w-6 animate-spin" />
                        </div>
                      ) : (
                        <GentlyTable
                          showDownload={false}
                          data={supplierPayments || []}
                          columns={paymentColumns}
                          searchFields={['type', 'amount']}
                          searchPlaceholder="Search payments..."
                          emptyMessage="No supplier payments recorded for this agreement"
                          actions={
                            <Button
                              onClick={paymentHandlers.create}
                              variant="outline"
                              size="sm"
                              className="gap-2"
                            >
                              <PlusIcon className="h-4 w-4" />
                              Record Payment
                            </Button>
                          }
                          summaryRow={{
                            date: (
                              <span className="text-sm font-bold">Total</span>
                            ),
                            amount: (
                              <span className="text-sm font-bold text-green-600">
                                $
                                {new Intl.NumberFormat('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(
                                  supplierPayments?.reduce(
                                    (sum, payment) => sum + payment.amount,
                                    0,
                                  ) || 0,
                                )}
                              </span>
                            ),
                            type: '',
                            memo: '',
                            document: '',
                            actions: '',
                          }}
                        />
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            </div>
          </Element>

          {/* Reconciliation Section */}
          <Element name="reconciliation">
            <RebateReconciliationSection
              reconciliationRef={reconciliationRef}
              isExpanded={expandedSections.has('reconciliation')}
              onToggle={handleSectionToggle('reconciliation')}
              forecastData={forecastData}
              supplierPayments={supplierPayments}
              loading={forecastLoading || paymentsLoading}
              agreement={agreement}
              onCloseAgreement={handleOpenCloseAgreementModal}
              onReopenAgreement={handleReopenAgreement}
              rules={
                rebateRules?.map((r) => ({ id: r.id, name: r.name })) || []
              }
              divisions={simpleDivisions || []}
            />
          </Element>

          {/* Vendor Reported Amounts Section */}
          <VendorReportedAmountsSection
            invoicesRef={invoicesRef}
            isExpanded={expandedSections.has('vendor_reported_amounts')}
            onToggle={handleSectionToggle('vendor_reported_amounts')}
            invoices={invoicesData?.invoices}
            invoicesLoading={invoicesLoading}
            lineItems={lineItemsData?.lineItems}
            lineItemsLoading={lineItemsLoading}
            lastDataIngestionDate={lastDataIngestionDate}
            isCumulative={isCumulative}
            selectedYear={selectedYear}
            agreementStartDate={agreement?.start_date}
            selectedDivisionIds={selectedDivisionIds}
            onViewDocument={handleViewDocument}
            onDeleteInvoice={invoiceDeleteModal.initiateDelete}
            showMigrationWizard={
              agreement?.status === 'finalized' &&
              versions &&
              versions.length > 1
            }
            onOpenMigrationWizard={() => setIsMigrationWizardOpen(true)}
            supplierId={agreement?.supplier?.id}
          />

          {/* Rebate Agreement Tags Section - moved to AgreementHeader */}
        </div>

        {/* Page Navigator */}
        {/* <PageNavigator
          sections={navigationSections}
          onNavigate={handleNavigateToSection}
          onToggleSection={handleToggleSection}
          mode="sidebar"
          positioning="overlay"
          storageKey="rebateAgreementPageNavigator"
        /> */}

        {/* Modals */}
        <ProductGroupModal
          open={productGroupModal.open}
          onClose={productGroupHandlers.closeModal}
          supplierId={agreement?.supplier.id || ''}
          productGroup={productGroupModal.editing ?? undefined}
          onSuccess={productGroupHandlers.onSuccess}
        />

        <SupplierPaymentModal
          open={paymentModal.open}
          onClose={paymentHandlers.closeModal}
          agreementId={id}
          onSuccess={paymentHandlers.onSuccess}
          defaultTaxRate={companyData?.metadata?.defaultTaxRate}
        />

        <PaymentAllocationModal
          open={allocationModalOpen}
          onClose={handleCloseAllocationModal}
          payment={selectedPaymentForAllocation}
          rules={(rebateRules || []) as unknown as RebateRuleWithId[]}
          onSuccess={handleAllocationSuccess}
          calculatedBreakdowns={calculatedBreakdowns}
          forecastDates={forecastData?.forecastDates}
        />

        <ConfirmDialog
          open={productGroupModal.deleteConfirmOpen}
          onClose={productGroupHandlers.cancelDelete}
          onConfirm={productGroupHandlers.confirmDelete}
          title="Delete Product Group"
          message={`Are you sure you want to delete "${productGroupModal.itemToDelete?.name}"?`}
          confirmText="Delete"
          loading={productGroupModal.deleting}
          dangerous={true}
        />

        <ConfirmDialog
          open={rebateRuleModal.deleteConfirmOpen}
          onClose={rebateRuleHandlers.cancelDelete}
          onConfirm={rebateRuleHandlers.confirmDelete}
          title="Delete Rebate Rule"
          message={`Are you sure you want to delete "${rebateRuleModal.itemToDelete?.name}"?`}
          confirmText="Delete"
          loading={rebateRuleModal.deleting}
          dangerous={true}
        />

        <ConfirmDialog
          open={paymentModal.deleteConfirmOpen}
          onClose={paymentHandlers.cancelDelete}
          onConfirm={paymentHandlers.confirmDelete}
          title="Delete Payment"
          message={`Are you sure you want to delete this payment?`}
          confirmText="Delete"
          loading={paymentModal.deleting}
          dangerous={true}
        />

        <DeleteAgreementConfirmDialog
          open={agreementDeleteModal.deleteConfirmOpen}
          onClose={agreementHandlers.cancelDelete}
          onConfirm={agreementHandlers.confirmDelete}
          agreementName={agreement?.name}
          loading={agreementDeleteModal.deleting}
        />

        {/* Invoice Delete Modal */}
        <DeleteRebateInvoiceModal
          open={invoiceDeleteModal.modalOpen}
          invoiceInfo={invoiceDeleteModal.invoiceInfo}
          loading={invoiceDeleteModal.isDeleting}
          showAdvanced={invoiceDeleteModal.showAdvanced}
          forceDeleteConfirmText={invoiceDeleteModal.forceDeleteConfirmText}
          onSetShowAdvanced={invoiceDeleteModal.setShowAdvanced}
          onSetForceDeleteConfirmText={
            invoiceDeleteModal.setForceDeleteConfirmText
          }
          onConfirmDelete={invoiceDeleteModal.confirmDelete}
          onCancel={invoiceDeleteModal.closeModal}
        />

        {/* CSV Migration Wizard Modal */}
        {versions && agreement && (
          <CSVMigrationWizard
            open={isMigrationWizardOpen}
            onOpenChange={setIsMigrationWizardOpen}
            versionGroupId={agreement.original_agreement_id}
            currentVersionNumber={currentVersionNumber}
            availableSourceVersions={versions.filter(
              (v) =>
                v.versionNumber < currentVersionNumber &&
                v.status === 'finalized',
            )}
          />
        )}

        {/* Supplier Change Confirmation Modal */}
        {agreement && newSupplierData && (
          <SupplierChangeConfirmationModal
            open={supplierChangeModalOpen}
            onClose={handleSupplierChangeCancel}
            onConfirm={handleSupplierChangeConfirm}
            currentSupplierName={agreement.supplier?.name || 'Unknown'}
            newSupplierName={newSupplierData.name}
            loading={reprocessMutation.isPending}
          />
        )}

        {/* Close Agreement Modal */}
        <CloseAgreementModal
          open={closeAgreementModalOpen}
          onClose={handleCloseAgreementModal}
          onConfirm={handleConfirmCloseAgreement}
          expectedAmount={closeAgreementSummary.expected}
          allocatedAmount={closeAgreementSummary.allocated}
          writeOffAmount={closeAgreementSummary.writeOff}
          loading={closeAgreementMutation.isPending}
        />

        {/* Floating Action Button - Show when forecast data or line items are available */}
        {(forecastData || lineItemsData) && (
          <ForecastControlsFAB
            isCumulative={isCumulative}
            onCumulativeChange={setIsCumulative}
            filterGroups={filterGroups}
            activeFilterCount={activeFilterCount}
            onClearAll={clearAllFilters}
            visibleLines={visibleLines}
            onVisibleLinesChange={setVisibleLines}
            primaryLine={primaryLine}
            onPrimaryLineChange={(line) => setPrimaryLine(line as LineType)}
            hasPreviousYearData={false}
            onReset={handleResetFAB}
            isResetAvailable={isResetAvailable}
            selectedYear={selectedYear}
            availablePredictionAlgorithms={availablePredictionAlgorithms}
          />
        )}

        {/* AI Chat Assistant */}
        <ForegroundAgentChat
          moduleKey="rebates"
          currentPage={`/rebates/agreements/${id}`}
          onNavigate={(path, options) =>
            navigate({
              to: path,
              search: options?.search as Record<string, unknown>,
            })
          }
          onUpdateFilters={handleAIFilterUpdate}
        />
      </div>
    </DashboardLayout>
  );
};

export default RebateAgreementDetailPage;
